// MACHINERY V2 (Stroje) — Fleet V1 card BELOW 1280 (M2.16b).
//
// Rendered truth: mobile/screenshots/05-v1-phone-claude.png +
// 06-v1-phone-machineid.png (phone, both palettes), 07-v1-tablet-768.png (2-col)
// and 08-v1-tablet-1024.png (3-col) — all four viewed before this file was
// written, per the view-before-code rule. Reference impl:
// mobile/machinery-mobile.jsx MbV1Card (:167-208); VALUES taken verbatim, the
// structure rebuilt on our contract.
//
// >>> IT IS ONE CARD FOR THE WHOLE BELOW-1280 TIER, NOT A "PHONE CARD". <<<
// Boards 07 and 08 draw THIS composition at 768 and at 1024, two-up and
// three-up — not the desktop FleetCard reflowed. So FleetGrid renders this
// below 1280 and FleetCard at >= 1280, and the tier boundary is the same one
// FleetShell already branches the chrome on.
//
// WHAT THE DESKTOP CARD CARRIES AND THIS ONE DOES NOT, and why each:
//   · the 2-column KV identity grid (S/N, interné č., značka, systém, rok, RPM)
//     — the board replaces all six with ONE mono line, `S/N … · lokácia`. Six
//     labelled cells at 390px is four wrapped rows of 10px labels;
//   · CapabilityBadges. NOT an oversight and NOT "mobile does not need
//     readiness": they are 14-20px overlay BUTTONS, and the law amended at
//     M2.16a's fix pass is ">= 44px BOTH axes below 1280". A 44px target inside
//     a 74x56 photo well would cover the photo, and shipping a 14px tap target
//     to satisfy the drawing would break the law the same pass wrote. Readiness
//     stays reachable below 1280 through the machine profile's readiness card
//     (readiness/ReadinessProfileCardV2.tsx), so the SIGNAL is re-homed rather
//     than removed — the M2.16a rule. The board draws none either.
// WHAT IT KEEPS THAT THE BOARD DOES NOT DRAW: the NEW badge, the health dot,
// the group/S-N-missing chips and the decommission note (FleetCardMeta, passed
// `inset={0}` + `gapBottom={0}` because the padded content column below supplies
// both — see the F1 block at that column). Same rule in the other direction —
// the board's mock fleet has no decommissioned machine and no group, but the
// ROZSAH section of the mobile sheet reaches the Vyradené scope, and a dashed
// 65%-opacity card with no note explains nothing.
//
// STATUS RENDERING IS NOT DECIDED HERE. Every paint comes from
// deriveCardPresentation(telemetry) — the M2.1 contract. This component reads
// its flags and never re-tests `alarms.length`, `conn` or `status`. In
// particular the board's own `conn ? … : …` inline tests are NOT ported: that is
// the split brain M2.1 closed.
//
// >>> UNCONNECTED IS NEUTRAL, ALWAYS. <<< No status colour anywhere on the card:
// the 3px bar falls back to var(--border), the chip row prints the state's own
// mono label instead of a badge, and the footer prints the explainer. This is a
// LOCKED rule (§4.3), not a style preference.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../shared/components/gallery";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import ConnDot from "./ConnDot";
import FleetCardMenu, { FleetMenuAction } from "./FleetCardMenu";
import { FleetCardChips, FleetCardDecommissionNote } from "./FleetCardMeta";
import StatusBadge, { AwaitingBadge } from "./StatusBadge";
import { HealthDot, MachinePhoto, NewBadge } from "./fleetCardAtoms";
import { statusColors } from "./cardStateStyles";
import { MachineryV2Telemetry, deriveCardPresentation } from "./cardStates";
import { cardStateLabelKey, showsStatusChip } from "./fleetActivity";
import CompactCardFooter from "./fleetCardCompactFooter";

/** The card's own horizontal padding. */
const INSET = 13;

/** The card's own vertical padding. It is on the CONTENT COLUMN, unconditional,
 *  and that is review finding F1's fix — see the block comment at the column. */
const PAD_Y = 11;

/** Vertical rhythm between the header block and whichever trailing rows render.
 *  It was previously the header's `padding-bottom: 0` plus the NEXT element's
 *  own `padding-top: 9` — an arrangement with no answer when the next element
 *  was null. */
const ROW_GAP = 9;

export interface FleetCardCompactProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  isNew: boolean;
  actions: FleetMenuAction[];
  onOpenProfile: () => void;
}

const FleetCardCompact: React.FC<FleetCardCompactProps> = ({
  machine, telemetry, isNew, actions, onOpenProfile,
}) => {
  const { translate } = useTranslation("myMachinery");
  const p = deriveCardPresentation(telemetry);
  const decommissioned = !!machine.isDecommissioned;
  const snMissing = !machine.serialNumber && !decommissioned;

  // The SAME shared 4-tier fallback FleetCard uses — machine files -> image ->
  // catalog hero -> catalog rows. Not a second resolution chain.
  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  const location = machine.locationName
    ? machine.parentLocationName
      ? machine.parentLocationName + " › " + machine.locationName
      : machine.locationName
    : null;
  // The board's one mono meta line: `S/N 34234 · Závod Brno 1`. Segments are
  // dropped rather than printed empty, so a machine with neither renders no
  // line at all instead of "S/N — · ".
  const meta = [
    machine.serialNumber ? "S/N " + machine.serialNumber : null,
    location,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article
      onClick={onOpenProfile}
      data-machine-id={machine.id}
      style={{
        position: "relative", background: "var(--card)", borderRadius: 12,
        border: decommissioned
          ? "1px dashed var(--border)"
          : "1px solid " + (snMissing ? "var(--amber)" : "var(--border)"),
        // overflow visible so the kebab's portalled menu escapes the card.
        overflow: "visible", boxShadow: "var(--shadow)", display: "flex",
        flexDirection: "column", width: "100%", minWidth: 0, cursor: "pointer",
        opacity: decommissioned ? 0.65 : 1,
      }}
    >
      {/* THE 3px STATUS TOP BAR — the board's one piece of card chrome the
          desktop card has no equivalent for. `p.badgeStatus` is the contract's
          verdict, so `unconnected`, `awaiting` and a status-less `stale` all
          fall to the neutral border colour rather than asserting a status we
          do not have. The card is `overflow: visible` (the kebab's menu has to
          escape it), so the bar cannot be clipped to the card's corners and
          carries the top radius itself. */}
      <div
        aria-hidden
        style={{
          height: 3,
          flexShrink: 0,
          borderRadius: "12px 12px 0 0",
          background: p.badgeStatus
            ? statusColors(p.badgeStatus, p.staleLook).fg
            : "var(--border)",
        }}
      />

      {/* >>> ONE PADDED COLUMN, AND THE PADDING IS UNCONDITIONAL (F1). <<<
          BOTH trailing rows are optional — the footer returns null when the
          activity resolves to nothing, the chip row when there are no groups, a
          serial and no decommission — and on today's wire that pairing is the
          COMMON case, not an edge (the gateway summary carries no program, no
          alarm text, no freshness stamp; fleetTelemetry.ts's header). This block
          used to pad `11px 13px 0` and rely on whichever row happened to render
          to supply the bottom, so the DEFAULT card on every phone and tablet sat
          its 56px photo and its name hard against the border. The column now
          owns the padding and the gap and the rows own only their content
          (hence `gapBottom={0}` below); a flex `gap` contributes nothing for a
          null child, so all four combinations are padded. */}
      <div
        style={{
          display: "flex", flexDirection: "column", gap: ROW_GAP,
          padding: `${PAD_Y}px ${INSET}px`, minWidth: 0,
        }}
      >
        <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
          <MachinePhoto
            src={images.length > 0 ? images[0].src : null}
            alt={getMachineDisplayName(machine)}
            grayscale={decommissioned}
            width={74}
            height={56}
          />

          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em",
                color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getMachineDisplayName(machine)}
            </div>
            {meta && (
              <div
                style={{
                  fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--muted)",
                  marginTop: 2, whiteSpace: "nowrap", overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {meta}
              </div>
            )}

            {/* Status chip row, gated on `showsStatusChip` — the ONE predicate
                every fleet surface asks (fleetActivity.ts), NOT `showStatusRow`,
                which is true for a status-less `stale` machine and so rendered a
                StatusBadge that returned null: a bare dot and no chip, where V3's
                row on the same phone printed a mark (finding F7).
                THE MONO LABEL NAMES THE STATE, from the same vocabulary the
                filter rail, the sheet's STAV section and the strip use — so an
                unconnected machine prints exactly what it printed before (the
                SHIPPED key, never the board's second Slovak word for a canonical
                state), and a stale one is no longer called unconnected. The dot
                moved OUTSIDE the branch: separate signal (§4.3), and it renders
                nothing itself when `dotConn` is null (ConnDot.tsx:64). */}
            <div
              style={{
                display: "flex", alignItems: "center", gap: 7, marginTop: 6,
                flexWrap: "wrap", minWidth: 0,
              }}
            >
              {showsStatusChip(p) ? (
                p.showAwaitingBadge ? (
                  <AwaitingBadge />
                ) : (
                  <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
                )
              ) : (
                <span
                  style={{
                    fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)",
                    letterSpacing: "0.1em", textTransform: "uppercase",
                  }}
                >
                  {translate(cardStateLabelKey(p.state))}
                </span>
              )}
              <ConnDot state={p.dotConn} size={7} lastHeartbeat={telemetry.heartbeat} />
              {isNew && <NewBadge />}
            </div>
          </div>

          {/* Top-right cluster, laid out as ONE flex column so the health dot and
              the 44px kebab cannot overlap the way the live grid's absolute
              corner stack could. */}
          <div
            style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 6, flexShrink: 0,
            }}
          >
            {/* touch: the >= 44px law below 1280 — see FleetCardMenu's prop note.
                The menu's own click AND Enter/Space guards live inside that
                component — the trigger stops click at FleetCardMenu.tsx:164 and
                Enter/Space at :166-168, and its portalled Content stops click at
                :200 and Enter/Space at :217-219. All four re-read while writing
                this line: the two Content numbers were STALE (:197 / :213-216)
                and are corrected here. That is why this navigating card needs no
                wrapper guard of its own. stopPropagation only, never
                preventDefault: the browser synthesises the button's click FROM
                that key event. */}
            <FleetCardMenu
              actions={actions}
              ariaLabel={translate("myMachinery:v2.cardMenu")}
              touch
            />
            {!decommissioned && <HealthDot openReports={machine.openServiceReportsCount} />}
          </div>
        </div>

        <CompactCardFooter presentation={p} telemetry={telemetry} />

        {/* inset 0 + gapBottom 0: the column above supplies both. See the F1
            block comment and FleetCardMeta's `gapBottom` note. */}
        <FleetCardChips
          machine={machine}
          snMissing={snMissing}
          decommissioned={decommissioned}
          inset={0}
          gapBottom={0}
        />
        {decommissioned && (
          <FleetCardDecommissionNote machine={machine} inset={0} gapBottom={0} />
        )}
      </div>
    </article>
  );
};

export default FleetCardCompact;
